import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FileText, Send, HeadphonesIcon, Building2 } from 'lucide-react';

const ConexusFormulaSection = () => {
  const { t } = useTranslation();

  const steps = [
    {
      icon: FileText,
      number: '01',
      title: t('formula.step1.title'),
      description: t('formula.step1.description'),
    },
    {
      icon: Send,
      number: '02',
      title: t('formula.step2.title'),
      description: t('formula.step2.description'),
    },
    {
      icon: HeadphonesIcon,
      number: '03',
      title: t('formula.step3.title'),
      description: t('formula.step3.description'),
    },
    {
      icon: Building2,
      number: '04',
      title: t('formula.step4.title'),
      description: t('formula.step4.description'),
    },
  ];

  return (
    <section className="py-24 bg-accent relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#112E4A] via-[#112E4A] to-[#0A1F3A]" />
      <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('formula.title')}
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            {t('formula.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="relative"
              >
                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-primary/30 z-0" />
                )}

                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-primary/30 transition-all duration-300 text-center relative z-10">
                  <div className="text-primary text-sm font-bold mb-4 tracking-widest">
                    {step.number}
                  </div>
                  <div className="bg-primary/20 w-14 h-14 rounded-xl flex items-center justify-center mb-5 mx-auto">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-white/85 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ConexusFormulaSection;
